/**
 * Robot vacuum controls (Roomba / Ecovacs Deebot).
 *
 * Unlike every other panel, this one never talks to our server for data: the
 * robots are only reachable from the home network, so the browser calls the
 * vacuum bridge running there (vacuum-bridge/ in this repo) directly. The
 * server only hands over the bridge URL and token via /internal/config.
 */

import { getVacuumBridge } from './state.js';

const BRAND_ICONS = {
  roomba: '🤖',
  ecovacs: '🧹',
};

const STATUS_LABELS = {
  cleaning: 'Cleaning',
  returning: 'Returning',
  docked: 'Docked',
  paused: 'Paused',
  idle: 'Idle',
  error: 'Needs help',
  offline: 'Offline',
};

// Long enough for the Ecovacs cloud round trip, short enough that a dead
// bridge does not leave a button spinning.
const REQUEST_TIMEOUT_MS = 10000;

// Commands in flight, keyed by vacuum id, so a poll that lands mid-command
// does not re-enable the button the user just pressed.
const pending = {};

/**
 * Escape text for interpolation into HTML. Robot names come from the vendor
 * apps, so they are not ours to trust.
 * @param {string} text - Raw text
 * @returns {string} HTML-safe text
 */
function escapeHtml(text) {
  return String(text)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * The one-line status for a card.
 * @param {object} vacuum - Vacuum entry from the bridge
 * @returns {string} Status text, with battery when known
 */
export function describeVacuum(vacuum) {
  const label = STATUS_LABELS[vacuum.status] || 'Unknown';
  const battery = vacuum.battery === null || vacuum.battery === undefined ? '' : ` · ${vacuum.battery}%`;

  return vacuum.status === 'offline' ? label : `${label}${battery}`;
}

/**
 * Which buttons make sense right now. Both stay visible so the layout does
 * not jump; the one that would do nothing is disabled.
 * @param {object} vacuum - Vacuum entry from the bridge
 * @returns {{start: boolean, dock: boolean}} Whether each action is enabled
 */
export function availableActions(vacuum) {
  if (vacuum.status === 'offline' || pending[vacuum.id]) {
    return { start: false, dock: false };
  }

  return {
    start: vacuum.status !== 'cleaning',
    dock: vacuum.status !== 'docked' && vacuum.status !== 'returning',
  };
}

/**
 * Render one vacuum card: icon, name, status, and the two buttons, all on a
 * single line to fit the Sensors panel's height budget.
 * @param {object} vacuum - Vacuum entry from the bridge
 * @returns {string} Card HTML
 */
export function renderVacuumCard(vacuum) {
  const icon = BRAND_ICONS[vacuum.brand] || '🧹';
  const actions = availableActions(vacuum);
  const id = escapeHtml(vacuum.id);
  const status = pending[vacuum.id] ? 'Sending…' : describeVacuum(vacuum);

  return `
    <div class="cell vacuum-holder vacuum-${escapeHtml(vacuum.status)}">
      <div class="vacuum-row">
        <span class="vacuum-icon">${icon}</span>
        <span class="vacuum-name">${escapeHtml(vacuum.name)}</span>
        <span class="vacuum-status">${status}</span>
        <button type="button" class="vacuum-btn" data-vacuum-id="${id}" data-action="start"
          aria-label="Start ${escapeHtml(vacuum.name)}" ${actions.start ? '' : 'disabled'}>▶</button>
        <button type="button" class="vacuum-btn" data-vacuum-id="${id}" data-action="dock"
          aria-label="Dock ${escapeHtml(vacuum.name)}" ${actions.dock ? '' : 'disabled'}>⌂</button>
      </div>
    </div>`;
}

/**
 * Call the bridge.
 * @param {string} path - Path below the bridge URL
 * @param {string} [method] - HTTP method
 * @returns {Promise<object>} Parsed JSON body
 */
async function bridgeRequest(path, method = 'GET') {
  const bridge = getVacuumBridge();
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

  try {
    const response = await fetch(`${bridge.bridgeUrl}${path}`, {
      method,
      headers: { Authorization: `Bearer ${bridge.token}` },
      signal: controller.signal,
    });
    const body = await response.json().catch(() => ({}));

    if (!response.ok) {
      throw new Error(body.error || `HTTP ${response.status}`);
    }

    return body;
  } finally {
    clearTimeout(timer);
  }
}

// Last list from the bridge, so a button press can re-render immediately.
let lastVacuums = [];

function render() {
  jQuery('#vacuum-list').html(lastVacuums.map(renderVacuumCard).join(''));
}

/**
 * Show a bridge-level failure in place of the cards.
 * @param {string} message - What went wrong
 */
function renderBridgeError(message) {
  jQuery('#vacuum-list').html(`
    <div class="cell vacuum-holder vacuum-error">
      <div class="vacuum-row">
        <span class="vacuum-icon">🧹</span>
        <span class="vacuum-name">Vacuums</span>
        <span class="vacuum-status">${escapeHtml(message)}</span>
      </div>
    </div>`);
}

/**
 * Fetch vacuum state from the bridge and refresh the cards.
 * @returns {Promise<void>}
 */
export async function updateVacuums() {
  // No bridge configured: leave the panel exactly as it was without this feature.
  if (!getVacuumBridge()) {
    return;
  }

  try {
    const data = await bridgeRequest('/vacuums');
    lastVacuums = data.vacuums || [];
    render();
  } catch (error) {
    console.error('[Vacuums] Bridge unreachable:', error);
    lastVacuums = [];
    renderBridgeError('Bridge unreachable');
  }
}

/**
 * Send start or dock to one vacuum, then refresh.
 * @param {string} vacuumId - Vacuum id from the bridge
 * @param {string} action - "start" or "dock"
 * @returns {Promise<void>}
 */
export async function sendVacuumCommand(vacuumId, action) {
  if (!getVacuumBridge() || pending[vacuumId] || !['start', 'dock'].includes(action)) {
    return;
  }

  pending[vacuumId] = true;
  render();

  try {
    await bridgeRequest(`/vacuums/${encodeURIComponent(vacuumId)}/${action}`, 'POST');
  } catch (error) {
    console.error(`[Vacuums] ${action} failed for ${vacuumId}:`, error);
  } finally {
    delete pending[vacuumId];
  }

  // The robot takes a moment to report its new state; refresh now for the
  // button, and again shortly for the status.
  await updateVacuums();
  setTimeout(updateVacuums, 5000);
}
